import cx from 'classnames'
import { type JSX, useState } from 'react'

import { paperStyles } from '../../libs/paperStyles'
import { zIndexStyles } from '../../libs/zIndexStyles'
import { typographyStyles } from '../../structure/typography/styles'
import { TableCellContext } from './tableCellContext'

export type TableProps = JSX.IntrinsicElements['table'] & {
  hasLeftAlignedColumns?: boolean
}

const Table = ({
  className,
  hasLeftAlignedColumns = false,
  ...props
}: TableProps) => (
  <table
    data-amaze-table
    data-has-left-aligned-columns={hasLeftAlignedColumns ? true : undefined}
    className={cx(
      'min-w-full relative',
      paperStyles({ borders: ['all'] }),
      // Remove border when inside a cardbody
      '[[data-amaze-cardbody]>&]:border-x-0',
      '[[data-amaze-cardbody]>&]:-mx-1.5',
      '[[data-amaze-cardbody]>&:first-child]:border-t-0',
      '[[data-amaze-cardbody]>&:last-child]:border-b-0',
      'has-[thead]:border-t-0', // remove top border if table has a thead
      'has-[tfoot]:border-b-0', // remove bottom border if table has a tfoot
      className,
    )}
    {...props}
  />
)

const stickyBorderStyles = (isHead = true) =>
  cx(
    // Using pseudo elements to create y borders to not collapse when sticky
    'relative',
    'before:content-[""] before:absolute before:left-0 before:w-full before:h-px before:top-0 before:z-2 before:bg-slate-200 dark:before:bg-slate-700',
    'after:content-[""] after:absolute after:left-0 after:w-full after:h-px after:bottom-0 after:z-2 after:bg-slate-200 dark:after:bg-slate-700',
    // Remove outer border when inside a cardbody
    isHead
      ? '[[data-amaze-cardbody]>[data-amaze-table]:first-child>&]:before:hidden'
      : '[[data-amaze-cardbody]>[data-amaze-table]:last-child>&]:after:hidden',
  )

type THeadProps = JSX.IntrinsicElements['thead']

const THead = ({ children, className, ref, ...props }: THeadProps) => (
  <thead
    ref={ref}
    className={cx(
      stickyBorderStyles(),
      className,
      'sticky top-0',
      zIndexStyles('sticky'),
    )}
    {...props}
  >
    {children}
  </thead>
)

const TBody = ({ className, ...props }: JSX.IntrinsicElements['tbody']) => (
  <tbody className={cx(className)} {...props} />
)

type TFootProps = JSX.IntrinsicElements['tfoot']

const TFoot = ({ className, ...props }: TFootProps) => (
  <tfoot
    className={cx(
      stickyBorderStyles(false),
      zIndexStyles('sticky'),
      'sticky bottom-0',
      className,
    )}
    {...props}
  />
)

const Tr = ({
  className,
  isRowClickable,
  isRowSelected,
  isRowHighlightedOnHover = true,
  ...props
}: JSX.IntrinsicElements['tr'] & {
  isRowClickable?: boolean
  isRowSelected?: boolean
  isRowHighlightedOnHover?: boolean
}) => {
  const [isHovered, setIsHovered] = useState(false)

  // oxlint-disable-next-line no-nested-ternary
  const mutuallyExclusiveStyleStates = isRowSelected
    ? 'isSelected' // we deliberately don't want to inherit the hover styles when a row is selected
    : isRowClickable
      ? 'isClickable'
      : 'default'

  return (
    <tr
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cx(
        paperStyles({ borders: ['bottom'] }),
        {
          isSelected:
            'cursor-pointer bg-primary/10 hover:bg-primary/20 active:bg-primary/20',
          isClickable: 'cursor-pointer hover:bg-primary/5 active:bg-primary/10',
          default: isRowHighlightedOnHover
            ? 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
            : '',
        }[mutuallyExclusiveStyleStates],
        'not-[a>&]:last:border-b-0 [a:last-child>&]:border-b-0', // tr can be wrapped in a link
        typographyStyles({ intensity: isHovered ? 'primary' : 'secondary' }),
        className,
      )}
      {...props}
    />
  )
}

type ThProps = {
  isSticky?: boolean
} & JSX.IntrinsicElements['th']

const Th = ({ className, isSticky, ...props }: ThProps) => (
  <th
    className={cx(
      className,
      typographyStyles({ weight: 'semibold', intensity: 'primary' }),
      'bg-inherit', // The row defines the background color, not the cell but we need to set it on the cell to enable sticky cells to inherit the row background color
      'p-1.5 text-left align-bottom whitespace-nowrap',
      'relative after:content-[""] after:absolute after:z-1 after:h-full after:top-0 after:w-px after:bg-slate-200 dark:after:bg-slate-700 last:after:hidden', // border-r
      'after:right-[-0.5px]', // ensure it has position similar to actual borders
      {
        'sticky left-0': isSticky,
      },
    )}
    {...props}
  />
)

type TdProps = {
  isSticky?: boolean
} & JSX.IntrinsicElements['td']

const Td = ({ className, isSticky, ...props }: TdProps) => (
  <TableCellContext value>
    <td
      className={cx(
        'in-data-has-left-aligned-columns:not-last:w-[20ch] in-data-has-left-aligned-columns:whitespace-nowrap', // if the table has left aligned columns, we want all but the last column to have 20ch width
        'transition-colors',
        isSticky && 'sticky left-0',
        'p-1.5 has-data-amaze-tablecell:p-1',
        'bg-inherit has-data-amaze-tablecell:bg-slate-100 has-data-amaze-tablecell:dark:bg-slate-800',
        'border-transparent has-data-amaze-tablecell:border-slate-200 has-data-amaze-tablecell:dark:border-slate-700 has-data-amaze-tablecell:border-l has-data-amaze-tablecell:border-r',
        className,
      )}
      {...props}
    />
  </TableCellContext>
)

export { Table, TBody, Td, TFoot, Th, THead, Tr }
